import React, { useEffect, useContext, useState } from "react";
import Text from "../components/Paragraph/Text.js";
import { ConfigContext } from "../App.js";
import { useStoreContent } from "../hooks/useQueries.js";
import { renderCardsSection } from "../functions/renderComponent.js";
import { Helmet } from "react-helmet-async";
import Loading from "../components/Loading/Loading.js";
import { TranslateContext } from "../App.js";
import CategoryButtons from "../components/CategoryButtons/CategoryButtons.js";
import Banner from "../components/Banner/Banner.js";

/**
 * Store page component.
 * Displays all products, categories, and featured items.
 * Uses React Query for cached data fetching.
 */
function Store() {
  const configuration = useContext(ConfigContext);
  const [featured, setFeatured] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isRendering, setIsRendering] = useState(true);
  const language = useContext(TranslateContext).language;

  // Use React Query hook for cached store data
  const { data: response, isLoading: dataLoading } = useStoreContent();

  useEffect(() => {
    const processStoreData = async () => {
      if (!response) return;
      
      setIsRendering(true);

      if (response.products) {
        const products = {
          cardProductSection: response.products,
          cardType: "product-sm",
          hasFilter: true,
        };

        setAllProducts(await renderCardsSection(products, false, false, language, true));
      }

      if (response.categories) {
        setAllCategories(<CategoryButtons categories={response.categories} />);
      }

      if (response.store?.featured) {
        let featuredSection = {
          title: null,
          button: { link: null, size: "medium", text: null },
          cardProductSection: [],
          cardType: "product",
          hasFilter: false,
        };
        if (response.store.featured.title[language]) {
          featuredSection.title = response.store.featured.title[language];
        }
        if (response.store.featured.button) {
          if (response.store.featured.button.link) {
            featuredSection.button.link = response.store.featured.button.link;
          }
          if (response.store.featured.button.text) {
            featuredSection.button.text = response.store.featured.button.text[language];
          }
        }
        if (response.store.featured.products) {
          featuredSection.cardProductSection = response.store.featured.products;
        }

        setFeatured(await renderCardsSection(featuredSection, true, true, language));
      }
      setIsRendering(false);
    };

    processStoreData();
  }, [response, language]);

  const pageContent = response?.store;
  const isLoading = dataLoading || isRendering;

  if (isLoading || !pageContent) {
    return <Loading />;
  }

  return (
    pageContent && (
      <>
        <Helmet>
          <title>{pageContent.title[language]}</title>
          <meta name="description" content={pageContent.metaDescription[language]} />
        </Helmet>
        {configuration?.checkoutBanner?.show && (
          <div className="row bg-[#fcf6ef]">
            <div className="row-container">
              <Banner
                type={configuration?.checkoutBanner?.type || "warning"}
                message={configuration?.checkoutBanner?.message}
              />
            </div>
          </div>
        )}
        <div className="row">
          <div className="row-container">
            <div className="column">
              <h1 className="h1-title" style={{ color: configuration.colors.secondary.hex }}>
                {pageContent.title[language]}
              </h1>
              {pageContent.onPageDescription && (
                <Text content={pageContent.onPageDescription[language]} align={pageContent.onPageDescription.justify} />
              )}
              <div
                className="flex gap-3 flex-wrap justify-center pb-1"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {allCategories}
              </div>
              <style>
                {`
                  .flex::-webkit-scrollbar {
                    display: none;
                  }
                `}
              </style>
              {allProducts}
              {featured}
            </div>
          </div>
        </div>
      </>
    )
  );
}

export default Store;
